import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import MovieCard from '@/components/feature/MovieCard';
import SectionHeading from '@/components/feature/SectionHeading';
import PlayModal from '@/components/feature/PlayModal';
import EmptyState from '@/components/feature/EmptyState';
import { movies } from '@/mocks/movies';

export default function MovieDetail() {
  const { id } = useParams();
  const [playOpen, setPlayOpen] = useState(false);
  const [toast, setToast] = useState('');

  const movie = movies.find((m) => m.id === id);

  const recommendations = useMemo(() => {
    if (!movie) return [];
    const sameCategory = movies.filter((m) => m.id !== movie.id && m.category === movie.category);
    const others = movies.filter((m) => m.id !== movie.id && m.category !== movie.category);
    return [...sameCategory, ...others].slice(0, 6);
  }, [movie]);

  if (!movie) {
    return (
      <div className="w-full bg-background-50 min-h-[60vh] flex items-center justify-center px-4">
        <EmptyState
          icon="ri-film-line"
          title="影片未找到"
          description="抱歉，你访问的影片可能已被移除或链接有误。"
          actionLabel="返回电影分类"
          onAction={() => {
            window.REACT_APP_NAVIGATE?.('/movies');
          }}
        />
      </div>
    );
  }

  const share = (label: string) => {
    setToast(`已模拟分享到${label}`);
    window.setTimeout(() => setToast(''), 1800);
  };

  return (
    <div className="w-full bg-background-50">
      <section className="relative w-full h-[320px] md:h-[460px] overflow-hidden">
        <img
          src={movie.backdrop}
          alt={`${movie.title} 剧照`}
          title={`${movie.title} - 电影详情`}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/70 to-background-50/30"></div>
      </section>

      <div className="w-full px-4 md:px-8">
        <div className="max-w-screen mx-auto">
          <nav className="text-xs md:text-sm text-foreground-600 pt-6 mb-6 flex items-center gap-1.5 flex-wrap">
            <Link to="/" className="hover:text-primary-400 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/movies" className="hover:text-primary-400 transition-colors">电影分类</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800">{movie.title}</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-8 -mt-28 md:-mt-40 relative z-10">
            <div className="mx-auto lg:mx-0 w-44 md:w-60 lg:w-full">
              <div className="w-full aspect-[2/3] rounded-lg overflow-hidden border border-background-300/60 bg-background-200">
                <img
                  src={movie.poster}
                  alt={`${movie.title} 海报`}
                  title={`${movie.title} - 高清在线电影`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="pt-0 lg:pt-24">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="px-2.5 py-1 rounded-md bg-primary-500 text-background-50 text-xs font-semibold">{movie.category}</span>
                {movie.genres.map((g) => (
                  <span key={g} className="px-2.5 py-1 rounded-md bg-secondary-800 text-secondary-200 text-xs">{g}</span>
                ))}
              </div>

              <h1 className="text-2xl md:text-4xl font-black text-foreground-950 mb-1">{movie.title}</h1>
              <p className="text-sm md:text-base text-foreground-600 mb-2">{movie.titleEn}</p>
              <p className="text-sm md:text-base text-foreground-700 mb-5">{movie.subtitle}</p>

              <div className="flex flex-wrap items-center gap-4 md:gap-6 text-sm text-foreground-700 mb-6">
                <span className="flex items-center gap-1.5">
                  <i className="ri-star-fill text-primary-500"></i>
                  <span className="text-primary-400 font-bold text-lg">{movie.rating.toFixed(1)}</span>
                </span>
                <span>{movie.year}</span>
                <span>{movie.region}</span>
                <span>{movie.duration} 分钟</span>
              </div>

              <div className="flex flex-wrap items-center gap-3 mb-8">
                <button
                  type="button"
                  onClick={() => setPlayOpen(true)}
                  className="px-6 py-3 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-play-fill text-xl"></i> 立即播放（演示）
                </button>
                <button
                  type="button"
                  onClick={() => share('片单')}
                  className="px-5 py-3 rounded-md border border-background-400/60 hover:border-primary-500 text-foreground-900 text-sm font-medium transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-add-line text-lg"></i> 加入片单
                </button>
              </div>

              <div className="bg-background-100 border border-background-300/50 rounded-lg p-5">
                <h2 className="text-base font-bold text-foreground-950 mb-3 flex items-center gap-2">
                  <i className="ri-information-line text-primary-500"></i> 影片信息
                </h2>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
                  <InfoRow label="导演" value={movie.director} />
                  <InfoRow label="类型" value={movie.genres.join(' / ')} />
                  <InfoRow label="地区" value={movie.region} />
                  <InfoRow label="上映年份" value={String(movie.year)} />
                  <InfoRow label="片长" value={`${movie.duration} 分钟`} />
                  <InfoRow label="主演" value={movie.cast.join('、')} />
                </dl>
              </div>
            </div>
          </div>

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8">
            <div>
              <SectionHeading title="剧情简介" />
              <p className="text-sm md:text-base text-foreground-700 leading-loose">{movie.synopsis}</p>

              <div className="mt-6 p-4 rounded-lg bg-accent-900/60 border border-background-300/50 flex items-start gap-3">
                <i className="ri-error-warning-line text-primary-500 text-lg mt-0.5"></i>
                <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">
                  温馨提示：本站提供的在线播放为前端演示效果，不包含真实影片播放数据与接口。页面内容均为演示数据，仅供学习交流使用。
                </p>
              </div>
            </div>

            <div>
              <h2 className="text-base font-bold text-foreground-950 mb-3 flex items-center gap-2">
                <i className="ri-share-line text-primary-500"></i> 分享影片
              </h2>
              <div className="flex flex-wrap gap-2">
                {[
                  { label: '微博', icon: 'ri-weibo-fill' },
                  { label: '微信', icon: 'ri-wechat-fill' },
                  { label: 'QQ', icon: 'ri-qq-fill' },
                  { label: '复制链接', icon: 'ri-link' },
                ].map((s) => (
                  <button
                    key={s.label}
                    type="button"
                    onClick={() => share(s.label)}
                    className="px-3 py-2 rounded-md bg-background-100 border border-background-300/60 hover:border-primary-500 text-foreground-800 text-xs transition-colors flex items-center gap-1.5 whitespace-nowrap cursor-pointer"
                  >
                    <i className={s.icon}></i> {s.label}
                  </button>
                ))}
              </div>
              <p className="text-xs text-foreground-600 mt-3 leading-relaxed">
                分享链接为演示功能，暂未对接真实社交平台接口。
              </p>
            </div>
          </div>

          <section className="mt-14 pb-14">
            <SectionHeading title="相关推荐" subtitle="喜欢这部电影的影迷还看了这些" moreTo="/movies" />
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
              {recommendations.map((m) => (
                <MovieCard key={m.id} movie={m} />
              ))}
            </div>
          </section>
        </div>
      </div>

      <PlayModal open={playOpen} onClose={() => setPlayOpen(false)} title={movie.title} />

      {toast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[70] px-4 py-2.5 rounded-md bg-background-100 border border-background-300/60 text-sm text-foreground-900 animate-fade-up">
          {toast}
        </div>
      )}
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2">
      <dt className="text-foreground-600 shrink-0 w-16">{label}</dt>
      <dd className="text-foreground-900 flex-1 min-w-0">{value}</dd>
    </div>
  );
}